import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CaseManagement } from "@/components/CaseManagement";
import { LeadsPanel } from "@/components/consultant/LeadsPanel";
import SecurityPanel from "@/components/consultant/SecurityPanel";
import SponsorshipPanel from "@/components/consultant/SponsorshipPanel";
import {
  Video, Link2, Shield, FileText, Briefcase, ClipboardList, ClipboardCheck, Send, Loader2,
  Calendar, CheckCircle, XCircle
} from "lucide-react";

export function DashboardBottomDialogs({
  user,
  isAdmin,
  authFetch,
  // Settings
  showSettingsDialog,
  setShowSettingsDialog,
  settings,
  setSettings,
  handleSaveSettings,
  // Meetings
  meetings,
  handleUpdateMeetingStatus,
  // Cases
  showCasesView,
  setShowCasesView,
  // Leads
  showLeadsPanel,
  setShowLeadsPanel,
  // Send Intake to Client
  sendIntakeClient,
  setSendIntakeClient,
  intakeMessage,
  setIntakeMessage,
  handleSendIntakeToExistingClient,
  sendingIntakeToClient,
  // Security
  showSecurityPanel,
  setShowSecurityPanel,
  // Sponsorship
  showSponsorshipPanel,
  setShowSponsorshipPanel
}) {
  return (
    <>
      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="p-5 border-b">
            <DialogTitle>Consultant Settings</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {/* Meeting & Calendar Links */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                <Video className="w-4 h-4 text-blue-500" /> Meeting Configuration
              </h3>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500">1:1 Meeting Link (Zoom, Google Meet, etc.)</Label>
                <Input
                  value={settings.meeting_link}
                  onChange={(e) => setSettings({ ...settings, meeting_link: e.target.value })}
                  placeholder="https://zoom.us/j/your-meeting-id"
                  data-testid="settings-meeting-link-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500">Calendar Booking Link (Optional)</Label>
                <Input
                  value={settings.calendar_link}
                  onChange={(e) => setSettings({ ...settings, calendar_link: e.target.value })}
                  placeholder="https://calendly.com/your-name"
                  data-testid="settings-calendar-link-input"
                />
              </div>
              <Button 
                onClick={handleSaveSettings}
                className="w-full bg-[#333333] hover:bg-[#444444] text-white"
                data-testid="save-settings-btn"
              >
                Save Settings
              </Button>
            </div>

            {/* Manage Scheduled Meetings */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-500" /> Scheduled Meetings ({(meetings || []).length})
              </h3>
              {(!meetings || meetings.length === 0) ? (
                <p className="text-neutral-500 text-sm text-center py-4">No meetings scheduled</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {meetings.map(meeting => {
                    const status = meeting.status || "scheduled";
                    const statusColors = {
                      scheduled: "bg-blue-50 border-blue-200",
                      checked_in: "bg-emerald-50 border-emerald-200",
                      completed: "bg-green-50 border-green-200 opacity-70",
                      cancelled: "bg-red-50 border-red-200 opacity-50",
                    };
                    const badgeColors = {
                      scheduled: "bg-blue-100 text-blue-700",
                      checked_in: "bg-emerald-100 text-emerald-700",
                      completed: "bg-green-100 text-green-700",
                      cancelled: "bg-red-100 text-red-700",
                    };
                    return (
                      <div key={meeting.meeting_id} className={`p-3 rounded-lg border ${statusColors[status] || statusColors.scheduled}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-neutral-900 truncate">{meeting.title}</p>
                            <p className="text-xs text-neutral-500">
                              {meeting.client?.name || "Client"} — {meeting.date} at {meeting.time} ({meeting.duration_minutes}min)
                            </p>
                          </div>
                          <Badge className={`text-[10px] flex-shrink-0 ${badgeColors[status] || ""}`}>
                            {status.replace("_", " ")}
                          </Badge>
                        </div>
                        {status !== "completed" && status !== "cancelled" && handleUpdateMeetingStatus && (
                          <div className="flex gap-1.5 mt-2">
                            {status === "scheduled" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                onClick={() => handleUpdateMeetingStatus(meeting.meeting_id, "checked_in")}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Check In
                              </Button>
                            )}
                            {(status === "scheduled" || status === "checked_in") && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => handleUpdateMeetingStatus(meeting.meeting_id, "completed")}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Complete
                              </Button>
                            )}
                            {status === "scheduled" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  const reason = prompt("Reason for cancellation (optional):");
                                  handleUpdateMeetingStatus(meeting.meeting_id, "cancelled", reason);
                                }}>
                                <XCircle className="w-3 h-3 mr-1" /> Cancel
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cases/CRM Dialog */}
      <Dialog open={showCasesView} onOpenChange={setShowCasesView}>
        <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-emerald-600" />
              Case Management & CRM
              {isAdmin && <Badge className="ml-2 bg-emerald-600">Admin View</Badge>}
            </DialogTitle>
            <DialogDescription>
              {isAdmin 
                ? "Full system oversight - manage all cases, clients, and consultants"
                : "Manage your assigned cases and client interactions"
              }
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            <CaseManagement user={user} isAdmin={isAdmin} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Assessment Leads Dialog */}
      <Dialog open={showLeadsPanel} onOpenChange={setShowLeadsPanel}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <ClipboardCheck className="w-5 h-5 mr-2 text-blue-600" />
              Assessment Leads
            </DialogTitle>
            <DialogDescription>
              Leads from the Business Clarity Assessment - follow up to convert them into clients
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            <LeadsPanel />
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Intake to Existing Client Dialog */}
      <Dialog open={!!sendIntakeClient} onOpenChange={(open) => !open && setSendIntakeClient(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Send Intake Form
            </DialogTitle>
            <DialogDescription>
              Send an intake form to {sendIntakeClient?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-900"><strong>Client:</strong> {sendIntakeClient?.name}</p>
              <p className="text-sm text-blue-700">{sendIntakeClient?.email}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="intake-message">Custom Message (Optional)</Label>
              <Textarea
                id="intake-message"
                placeholder="Add a personalized message to include with the intake form..."
                value={intakeMessage}
                onChange={(e) => setIntakeMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setSendIntakeClient(null); setIntakeMessage(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendIntakeToExistingClient}
              disabled={sendingIntakeToClient}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingIntakeToClient ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Intake Form</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security Panel Dialog */}
      <Dialog open={showSecurityPanel} onOpenChange={setShowSecurityPanel}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Security & Compliance
            </DialogTitle>
            <DialogDescription>
              Monitor authentication, audit logs, and security alerts
            </DialogDescription>
          </DialogHeader>
          <SecurityPanel authFetch={authFetch} />
        </DialogContent>
      </Dialog>

      {/* Sponsorship Panel Dialog */}
      <Dialog open={showSponsorshipPanel} onOpenChange={setShowSponsorshipPanel}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-neutral-900 border-neutral-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FileText className="w-5 h-5 text-emerald-400" />
              Sponsorship Gateway
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              Manage Institutional Sponsorship & Funding Readiness applications
            </DialogDescription>
          </DialogHeader>
          <SponsorshipPanel />
        </DialogContent>
      </Dialog>
    </>
  );
}
